import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {ADD_ITEM_BAR_POPUP_OFFSET, ADD_ITEM_BAR_TOP_OFFSET, AddItemOptionsProps} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import {Dropdown} from '@Components/dropdown-v2';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {onOpenFancyTextModal, onOpenMenuModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import {addTextSlideshowToPoster} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import {addTextToPoster} from '@PosterWhiteboard/items/text-item/text-item.class';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AddTextPopUpState} from '@Components/poster-editor/components/poster-add-item-bar/components/add-text-dropdown-options/add-text-dropdown-options.types';
import AiSubtitlePanel from '@Panels/ai-subtitle-panel/ai-subtitle-panel';
import styles from './add-text-dropdown-options.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {SubtitlesDropdownList} from '@Components/poster-editor/components/poster-add-item-bar/components/subtitles-dropdown-list/subtitles-dropdown-list';
import {setHasLanguageSelectorJustClosed} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {addNewManualTranscript, getAiSubtitlesPanelHeightClass} from '@Libraries/ai-transcript.library';
import {getUserId, isUserPremium} from '@Libraries/user.library';

export function AddTextDropdownOptions({popupClassName = '', arrowClasses = '', ...props}: AddItemOptionsProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const ref = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [popUpState, setPopupState] = useState<AddTextPopUpState>(AddTextPopUpState.DEFAULT_DROPDOWN);
  const aiSubtitleReduxState = useAppSelector((state) => {
    return state.aiSubtitle;
  });
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });
  const hasUserMediaPanelJustClosed = useAppSelector((state) => {
    return state.panels.panelHashmap['user-media-modal-panel'];
  });
  const hasLanguageSelectorJustClosed = useAppSelector((state) => {
    return state.aiSubtitle.aiSubtitlesPanel.hasLanguageSelectorJustClosed;
  });

  const getSubtitlesOption = (): ControlledListItemType[] => {
    return [
      {
        id: 'text-divider-subtitles-boundary-start',
        type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER,
      },
      {
        id: 'add-subtitles',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_subtitles'),
        subText: window.i18next.t('pmwjs_add_subtitles_option_description'),
        onClick: (id, e): void => {
          e?.preventDefault();
          e?.stopPropagation();

          setPopupState(AddTextPopUpState.SUBTITLES);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-captions" />,
        },
      },
      {
        id: 'text-divider-subtitles-boundary-end',
        type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER,
      },
    ];
  };

  const getOptionsForSubtitles = (): ReactElement<any> => {
    return (
      <SubtitlesDropdownList
        showBackButtonAsOptionItem
        onSelectAiSubtitlesOption={() => {
          setPopupState(AddTextPopUpState.AI_SUBTITLES);
        }}
        onSelectManualSubtitlesOption={() => {
          void addNewManualTranscript();
          closeDropdown();
        }}
        onBack={() => {
          setPopupState(AddTextPopUpState.DEFAULT_DROPDOWN);
        }}
      />
    );
  };

  const getOptionsForText = (): ReactElement<any> => {
    return (
      <ControlledList
        className="spacing-m-0"
        items={[
          {
            id: 'add-plain-text',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            text: window.i18next.t('pmwjs_plain_text'),
            subText: window.i18next.t('pmwjs_add_text_option_description'),
            onClick: (): void => {
              closeDropdown();
              addTextToPoster();
            },
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-text" />,
            },
          },
          {
            id: 'add-fancy-text',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            text: window.i18next.t('pmwjs_fancy_text'),
            subText: window.i18next.t('pmwjs_add_fancy_text_option_description'),
            onClick: (): void => {
              closeDropdown();
              onOpenFancyTextModal();
            },
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-fancy-text" />,
            },
          },
          ...getSubtitlesOption(),
          {
            id: 'add-slideshow-text',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            text: window.i18next.t('pmwjs_slideshow'),
            subText: window.i18next.t('pmwjs_add_a_text_slideshow'),
            onClick: (): void => {
              closeDropdown();
              addTextSlideshowToPoster();
            },
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-slideshow" />,
            },
          },
          {
            id: 'add-menu',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            text: window.i18next.t('pmwjs_menu_capitalize'),
            subText: window.i18next.t('pmwjs_add_menu_option_description'),
            onClick: (): void => {
              closeDropdown();
              trackPosterBuilderGA4Events(GA4EventName.MENU_MAKER_FROM_ADD_TEXT_MENU);
              onOpenMenuModal();
            },
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-food-menu" />,
            },
          },
        ]}
      />
    );
  };

  const getAiSubtitlesPanel = (): ReactElement<any> => {
    return (
      <AiSubtitlePanel
        contentClasses={styles.aiSubtitlePanel}
        onBack={() => {
          setPopupState(AddTextPopUpState.SUBTITLES);
        }}
        onDone={() => {
          setPopupState(AddTextPopUpState.SUBTITLES);
        }}
      />
    );
  };

  const getPopUpStateContent = (): ReactElement<any> => {
    switch (popUpState) {
      case AddTextPopUpState.SUBTITLES:
        return getOptionsForSubtitles();
      case AddTextPopUpState.AI_SUBTITLES:
        return getAiSubtitlesPanel();
      case AddTextPopUpState.DEFAULT_DROPDOWN:
        return getOptionsForText();
      default:
        throw new Error(`Unhandled pop up state for add text: ${popUpState}`);
    }
  };

  const shouldCloseOnOuterClick = (): boolean => {
    if (hasLanguageSelectorJustClosed) {
      setTimeout(() => {
        dispatch(setHasLanguageSelectorJustClosed(false));
      }, 10);

      return false;
    }

    return hasUserMediaPanelJustClosed === undefined;
  };

  const getAiSubtitlesPanelClasses = (): string => {
    return `${styles.aiSubtitlesPanelPopupContainerStyles} ${getAiSubtitlesPanelHeightClass(aiSubtitleState, Object.values(aiSubtitleReduxState.aiSubtitlesPanel.items).length, aiSubtitleReduxState.generationsBatch.totalItemsToGenerateSubtitlesFor, !isPremium.current)}`;
  };

  const closeDropdown = (): void => {
    if (ref.current) {
      $(ref.current).trigger('click');
    }
  };

  const getClassesForPopup = (): string => {
    switch (popUpState) {
      case AddTextPopUpState.AI_SUBTITLES:
        return getAiSubtitlesPanelClasses();
      case AddTextPopUpState.SUBTITLES:
        return styles.subtitleOptions;
      default:
        return '';
    }
  };

  return (
    <Dropdown
      doesCloseOnOuterClick={shouldCloseOnOuterClick()}
      doCloseOnInnerClick={false}
      className="js-toolbar-text"
      id="toolbar-text"
      key="toolbar-text"
      data-testid={props['data-testid']}
      alignment={ALIGNMENT_TYPE.RIGHT}
      selector={
        <div ref={ref}>
          <AddItemButton isSelected={isOpened} text={window.i18next.t('pmwjs_text')} icon="icon-text" />
        </div>
      }
      popup={getPopUpStateContent()}
      popupOffset={ADD_ITEM_BAR_POPUP_OFFSET}
      topOffset={ADD_ITEM_BAR_TOP_OFFSET}
      position={DROPDOWN_POSITION.FIXED}
      popUpHasCustomHeight
      maxHeight={650}
      popUpHasCustomWidth
      useDefaultArrow
      popupClassName={`${popupClassName} ${styles.popoverBaseStyle} ${getClassesForPopup()}`}
      popupSpacingClass={popUpState === AddTextPopUpState.AI_SUBTITLES ? 'spacing-p-0' : ''}
      arrowClasses={arrowClasses}
      onOpen={(): void => {
        if (!isOpened) {
          trackPosterBuilderGA4Events(GA4EventName.TEXT_MENU_OPEN);
          setIsOpened(true);
        }
      }}
      onClose={(): void => {
        if (isOpened) {
          setPopupState(AddTextPopUpState.DEFAULT_DROPDOWN);
          setIsOpened(false);
        }
      }}
    />
  );
}
